import { Box, BoxProps, Flex, Text, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { pxToRem } from 'styles/pxToRem';

interface LegalSectionProps extends Omit<BoxProps, 'title'> {
  /** Section index — a plain number is zero-padded to two digits (2 → "02"). */
  number: number | string;
  title: ReactNode;
  body: ReactNode;
  /** Optional callout boxes rendered, stacked, below the body. */
  callouts?: ReactNode[];
}

const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif";

const formatNumber = (value: number | string) =>
  typeof value === 'number' ? String(value).padStart(2, '0') : value;

/**
 * Shared building block for the legal/About sections: a numbered RTL header
 * (coral number + divider + title), a description body, and an optional slot
 * for one or more callout boxes. The single source of truth for section
 * styling — sections compose this rather than restyling per instance.
 */
export const LegalSection = ({ number, title, body, callouts, ...boxProps }: LegalSectionProps) => (
  <Box as='section' dir='rtl' {...boxProps}>
    <VStack align='stretch' spacing={{ base: '12px', md: '16px' }}>
      <Flex as='header' align='center' gap={{ base: '8px', md: '12px' }}>
        <Text
          as='span'
          color='var(--color-primary-500, #FF5C4F)'
          fontFamily={FONT_FAMILY}
          fontWeight='700'
          fontSize={pxToRem(18)}
          lineHeight='1.3'
          letterSpacing='-0.01em'
          textAlign='start'
          flexShrink={0}
          sx={{ leadingTrim: 'none', fontVariantNumeric: 'tabular-nums' }}
        >
          {formatNumber(number)}
        </Text>
        <Box
          as='span'
          aria-hidden
          flexShrink={0}
          h='2px'
          w={{ base: '20px', md: '24px' }}
          borderRadius='full'
          bg='var(--color-primary-500, #FF5C4F)'
        />
        <Text
          as='h2'
          flex='1'
          minW={0}
          color='var(--text-primary, #1B1917)'
          fontFamily={FONT_FAMILY}
          fontWeight='700'
          fontSize={pxToRem(18)}
          lineHeight='1.3'
          letterSpacing='-0.01em'
          textAlign='start'
          sx={{ leadingTrim: 'none' }}
        >
          {title}
        </Text>
      </Flex>

      <Box
        as='div'
        color='var(--text-secondary, #57534D)'
        fontFamily={FONT_FAMILY}
        fontWeight='400'
        fontSize={pxToRem(13)}
        lineHeight='1.5'
        letterSpacing='0.01em'
        textAlign='start'
        sx={{ leadingTrim: 'none' }}
      >
        {body}
      </Box>

      {callouts && callouts.length > 0 && (
        <VStack
          align='stretch'
          spacing={{ base: '12px', md: '16px' }}
          mt={{ base: '4px', md: '8px' }}
        >
          {callouts.map((callout, index) => (
            <Box key={index}>{callout}</Box>
          ))}
        </VStack>
      )}
    </VStack>
  </Box>
);
